/* ZOR — size chart + deterministic size finder.
   Reads the JSON blobs emitted by snippets/zor-fit-panel.liquid; no third-party
   service and no measurements ever leave the browser. */
/* ---------- garment-aware sizing maths (pure; window.ZorFitMath) ----------

   The finder first places the shopper on the body chart (height + weight +
   shape -> a continuous size position). From that position it estimates the
   shopper's body measurement, then sizes against THIS product's own
   finished-garment chart (custom.garment_chart):

   - Elastic waist ranges ("28-30"): those are body waists the band fits, so
     pick the first size whose range reaches the shopper's waist.
   - Single measurements (tops, leggings): each factory style is graded so its
     M fits a body-chart M, so the style's built-in room ("ease") is learned
     from its own chart and the size whose garment best matches
     body + ease wins. Boxy and compression styles therefore size by their
     own numbers rather than by one generic table.

   The shopper's fit preference (closer / as designed / looser) then moves the
   result one size. */
(function (root) {
  'use strict';

  function mid(v) {
    if (Array.isArray(v)) return (v[0] + v[1]) / 2;
    return typeof v === 'number' ? v : null;
  }

  // Body measurement at a continuous size position, interpolating the chart's
  // range midpoints and extrapolating linearly past either end.
  function bodyAt(chart, key, pos) {
    var pts = chart.rows.map(function (r) { return mid(r[key]); });
    if (pts.some(function (v) { return v == null; })) return null;
    var n = pts.length;
    if (n === 1) return pts[0];
    if (pos <= 0) return pts[0] + pos * (pts[1] - pts[0]);
    if (pos >= n - 1) return pts[n - 1] + (pos - (n - 1)) * (pts[n - 1] - pts[n - 2]);
    var i = Math.floor(pos), f = pos - i;
    return pts[i] + f * (pts[i + 1] - pts[i]);
  }

  function parseCell(v) {
    if (typeof v === 'number') return { lo: v, hi: v, range: false };
    var m = String(v).match(/^\s*([\d.]+)\s*[-–]\s*([\d.]+)\s*$/);
    if (m) return { lo: parseFloat(m[1]), hi: parseFloat(m[2]), range: true };
    var x = parseFloat(v);
    return isNaN(x) ? null : { lo: x, hi: x, range: false };
  }

  /* chart: body chart; garment: {cols:[{label,half}], rows:[[size,...]]}
     isBottom: product is a bottom; pos: continuous body position (shape applied)
     pref: -1 closer, 0 as designed, +1 looser
     -> { index, size, key, body, garment, ease, kind, beyondTop, belowBottom } */
  function recommend(chart, garment, isBottom, pos, pref) {
    if (!garment || !garment.rows || !garment.rows.length) return null;
    var labels = garment.cols.map(function (c) { return String(c.label).toLowerCase(); });
    var col = -1, bodyKey;
    if (isBottom) {
      col = labels.indexOf('waist'); bodyKey = 'waist';
    } else {
      col = labels.indexOf('chest');
      if (col < 0) col = labels.indexOf('waist'); // tank: hem width stands in for chest
      bodyKey = 'bust';
    }
    if (col < 0) return null;
    var half = !!garment.cols[col].half;
    var cells = garment.rows.map(function (r) {
      var c = parseCell(r[col + 1]);
      if (!c) return null;
      var k = half ? 2 : 1;
      return { size: r[0], lo: c.lo * k, hi: c.hi * k, range: c.range };
    });
    if (cells.some(function (c) { return !c; })) return null;

    var body = bodyAt(chart, bodyKey, pos);
    if (body == null) return null;
    var last = cells.length - 1;
    var idx, kind, beyondTop = false, belowBottom = false;

    if (cells[0].range) {
      kind = 'elastic';
      idx = -1;
      for (var i = 0; i <= last; i++) { if (cells[i].hi >= body - 0.25) { idx = i; break; } }
      if (idx < 0) { idx = last; beyondTop = body > cells[last].hi + 1; }
      belowBottom = body < cells[0].lo - 2;
    } else {
      kind = 'measured';
      // the style's own room: garment minus body-chart midpoint, per shared size
      var eases = [];
      cells.forEach(function (c) {
        for (var j = 0; j < chart.rows.length; j++) {
          if (String(chart.rows[j].size).toUpperCase() === String(c.size).toUpperCase()) {
            var b = mid(chart.rows[j][bodyKey]);
            if (b != null) eases.push(c.lo - b);
          }
        }
      });
      var ease = eases.length ? eases.reduce(function (a, b) { return a + b; }, 0) / eases.length : 0;
      var target = body + ease;
      var best = 0, bestD = Infinity;
      cells.forEach(function (c, k) {
        var d = Math.abs(c.lo - target);
        if (d < bestD - 1e-9 || (Math.abs(d - bestD) < 1e-9 && k > best)) { best = k; bestD = d; }
      });
      idx = best;
      var step = last > 0 ? (cells[last].lo - cells[last - 1].lo) : 2;
      beyondTop = target > cells[last].lo + step * 0.75;
      belowBottom = target < cells[0].lo - step * 0.75;
    }

    var shifted = Math.max(0, Math.min(last, idx + (pref || 0)));
    var c = cells[shifted];
    return {
      index: shifted, size: c.size, key: bodyKey, body: body,
      garment: c, ease: (c.range ? null : c.lo - body), kind: kind,
      half: half, beyondTop: beyondTop && pref >= 0, belowBottom: belowBottom && pref <= 0,
      rows: cells
    };
  }

  function feel(ease) {
    if (ease == null) return '';
    if (ease < -0.5) return 'a close fit that relies on the fabric\'s stretch';
    if (ease < 0.5) return 'a close, body-skimming fit';
    if (ease < 3) return 'a fitted feel';
    if (ease < 6) return 'a regular, easy fit';
    return 'a relaxed, roomy fit';
  }

  root.ZorFitMath = { bodyAt: bodyAt, parseCell: parseCell, recommend: recommend, feel: feel };
})(typeof window !== 'undefined' ? window : this);

(function () {
  'use strict';

  function parse(sel) {
    var el = document.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function init() {
    var wrap = document.querySelector('[data-zor-fit]');
    var modal = document.querySelector('[data-zor-fit-modal]');
    if (!wrap || !modal || wrap.dataset.zorFitInit) return;
    wrap.dataset.zorFitInit = '1';

    var charts = parse('[data-zor-size-charts]');
    var config = parse('[data-zor-fit-config]');
    if (!charts || !config) return;

    var chart = charts[config.chart] || charts['womens-top'];
    var garment = parse('[data-zor-garment]');
    var isBottom = /bottom/.test(String(config.chart));
    var sizes = config.sizes || [];

    // A size the product actually carries and has in stock.
    function stockOf(name) {
      for (var i = 0; i < sizes.length; i++) {
        if (String(sizes[i].v).toUpperCase() === String(name).toUpperCase()) return sizes[i].a;
      }
      return null; // not carried at all
    }

    /* ---------- open / close ---------- */
    var lastFocus = null;

    function open(tab) {
      lastFocus = document.activeElement;
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      showTab(tab || 'chart');
      var close = modal.querySelector('.zor-modal__close');
      if (close) close.focus();
    }

    function close() {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function showTab(name) {
      modal.querySelectorAll('[data-zor-tab]').forEach(function (t) {
        t.classList.toggle('is-on', t.getAttribute('data-zor-tab') === name);
      });
      modal.querySelectorAll('[data-zor-pane]').forEach(function (p) {
        p.classList.toggle('is-on', p.getAttribute('data-zor-pane') === name);
      });
    }

    document.querySelectorAll('[data-zor-fit-open]').forEach(function (b) {
      b.addEventListener('click', function () { open(b.getAttribute('data-zor-fit-open')); });
    });
    modal.querySelectorAll('[data-zor-fit-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    modal.querySelectorAll('[data-zor-tab]').forEach(function (t) {
      t.addEventListener('click', function () { showTab(t.getAttribute('data-zor-tab')); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    /* ---------- chart table ---------- */
    var table = modal.querySelector('[data-zor-chart]');
    if (table) {
      var head = '<thead><tr>' + chart.cols.map(function (c) {
        return '<th>' + c[1] + '</th>';
      }).join('') + '</tr></thead>';

      var body = '<tbody>' + chart.rows.map(function (row) {
        var stock = stockOf(row.size);
        var cells = chart.cols.map(function (c) {
          var v = row[c[0]];
          if (Array.isArray(v)) v = v[0] + '–' + v[1];
          return '<td>' + (v == null ? '—' : v) + '</td>';
        }).join('');
        return '<tr data-size="' + row.size + '"' + (stock ? '' : ' class="is-out"') + '>' + cells + '</tr>';
      }).join('') + '</tbody>';

      table.innerHTML = head + body;
    }

    /* ---------- deterministic recommendation ----------

       Height and weight together, not weight alone. Treating the torso as a
       cylinder of height H and girth G, mass scales with H * G^2, so
       G = k * sqrt(W / H). Two people share a girth when they share W / H,
       which means anyone can be mapped onto the chart's own weight bands by
       scaling their weight to the chart's median height:

         W_equivalent = W * (medianHeight / theirHeight)

       That keeps every number the buying team already tuned in the chart and
       adds no constants of its own. The body-shape avatar then shifts the
       result by a fraction of a size, which is why the band lookup returns a
       continuous position rather than a bare index. */

    // The charts now close both end bands explicitly, so this is a no-op for
    // them; it stays as a guard for any chart still written open-ended
    // ([0, 47.9], [75, 999]), giving those ends a real width so that a
    // position inside them still means something.
    function closedBands(bands) {
      var out = bands.map(function (b) { return [b[0], b[1]]; });
      var widths = [];
      out.forEach(function (b) {
        var w = b[1] - b[0];
        if (b[0] > 0 && w > 0 && w < 40) widths.push(w);
      });
      var typical = widths.length
        ? widths.reduce(function (a, b) { return a + b; }, 0) / widths.length
        : 10;
      var last = out.length - 1;
      if (out[0][0] <= 0) out[0][0] = out[0][1] - typical;
      if (out[last][1] - out[last][0] > 40) out[last][1] = out[last][0] + typical;
      return out;
    }

    // Continuous position on the size scale: a value at the centre of band i
    // returns exactly i, the band edges return i ± 0.5.
    function bandPosition(bands, value) {
      var b = closedBands(bands);
      for (var i = 0; i < b.length; i++) {
        if (value <= b[i][1] || i === b.length - 1) {
          var span = b[i][1] - b[i][0];
          var f = span > 0 ? (value - b[i][0]) / span : 0.5;
          f = Math.max(-0.5, Math.min(1.5, f));
          return i + f - 0.5;
        }
      }
      return 0;
    }

    function nearestStocked(index) {
      var rows = chart.rows;
      if (stockOf(rows[index].size)) return index;
      for (var step = 1; step < rows.length; step++) {
        var up = index + step;
        if (up < rows.length && stockOf(rows[up].size)) return up;   // size up before down
        var down = index - step;
        if (down >= 0 && stockOf(rows[down].size)) return down;
      }
      return -1;
    }

    var out = modal.querySelector('[data-zor-finder-out]');
    var err = modal.querySelector('[data-zor-finder-err]');
    var sizeEl = modal.querySelector('[data-zor-finder-size]');
    var whyEl = modal.querySelector('[data-zor-finder-why]');
    var applyBtn = modal.querySelector('[data-zor-finder-apply]');
    var go = modal.querySelector('[data-zor-finder-go]');

    function readField(name) {
      var el = modal.querySelector('[data-zor-f="' + name + '"]');
      if (!el) return null;
      var n = parseFloat(el.value);
      return isNaN(n) ? null : n;
    }

    function heightInches() {
      var ft = readField('ft');
      var inch = readField('in');
      if (ft == null && inch == null) return null;
      var total = (ft || 0) * 12 + (inch || 0);
      return total > 0 ? total : null;
    }

    function pickedShape() {
      var el = modal.querySelector('[data-zor-shape]:checked');
      return el ? el.value : null;
    }

    function shapeLabel(shape) {
      var el = modal.querySelector('[data-zor-shape][value="' + shape + '"]');
      var name = el && el.parentNode.querySelector('.zor-shape__name');
      return name ? name.textContent.toLowerCase() : shape;
    }

    function fail() {
      out.classList.remove('is-on');
      err.classList.add('is-on');
    }

    function recommend() {
      var height = heightInches();
      var weight = readField('weight');
      var shape = pickedShape();

      if (height == null || weight == null || !shape || !chart.weight) return fail();
      err.classList.remove('is-on');

      var median = chart.heightMedian || height;
      var pos = bandPosition(chart.weight, weight * (median / height));

      var deltas = chart.shape || {};
      var delta = typeof deltas[shape] === 'number' ? deltas[shape] : 0;

      // Keep the unclamped position: it is the only signal that the shopper
      // falls outside the size run, and saying so beats a confident wrong size.
      // Per-chart calibration from real try-ons: the women's run was landing a
      // full size big, so those charts carry "calibrate": -1.
      var raw = pos + delta + (typeof chart.calibrate === 'number' ? chart.calibrate : 0);

      // Product-specific sizing against this style's own garment chart.
      var prefEl = modal.querySelector('[data-zor-pref]:checked');
      var pref = prefEl ? parseInt(prefEl.value, 10) || 0 : 0;
      var g = garment && window.ZorFitMath ? window.ZorFitMath.recommend(chart, garment, isBottom, raw, pref) : null;
      if (g) return showGarment(g, shape, pref);
      var last = chart.rows.length - 1;
      var index = Math.max(0, Math.min(last, Math.round(raw)));
      var beyondTop = raw > last + 0.5;
      var belowBottom = raw < -0.5;
      var final = nearestStocked(index);

      if (final < 0) {
        sizeEl.textContent = '—';
        whyEl.textContent = 'Every size in your range is currently sold out.';
        applyBtn.hidden = true;
        out.classList.add('is-on');
        return;
      }

      var name = chart.rows[final].size;
      sizeEl.textContent = name;

      var label = shapeLabel(shape);
      var article = /^[aeiou]/.test(label) ? 'an' : 'a';
      var why = 'Based on your height and weight, with ' + article + ' ' + label + ' build.';
      if (beyondTop) {
        why += ' You are above our size range — ' + chart.rows[last].size + ' is the largest we make.';
      } else if (belowBottom) {
        why += ' You are below our size range — ' + chart.rows[0].size + ' is the smallest we make.';
      }
      if (final !== index) {
        // Not carried at all reads differently from carried-but-sold-out.
        why += stockOf(chart.rows[index].size) === null
          ? ' This piece is not made in ' + chart.rows[index].size + ', so this is the closest we offer.'
          : ' Your closest size is sold out, so this is the nearest available.';
      }
      whyEl.textContent = why;

      applyBtn.hidden = false;
      applyBtn.setAttribute('data-size', name);
      out.classList.add('is-on');

      // Highlight the recommended row on the chart tab.
      modal.querySelectorAll('.zor-chart tr[data-size]').forEach(function (tr) {
        tr.classList.toggle('is-rec', tr.getAttribute('data-size') === name);
      });
    }

    function half(n) { return Math.round(n * 2) / 2; }
    function inch(n) { return String(half(n)).replace(/\.0$/, '') + '\u2033'; }

    function showGarment(g, shape, pref) {
      // nearest stocked size, sizing up before down
      var pick = -1, rows = g.rows;
      if (stockOf(rows[g.index].size)) pick = g.index;
      for (var step = 1; pick < 0 && step < rows.length; step++) {
        if (g.index + step < rows.length && stockOf(rows[g.index + step].size)) pick = g.index + step;
        else if (g.index - step >= 0 && stockOf(rows[g.index - step].size)) pick = g.index - step;
      }
      if (pick < 0) {
        sizeEl.textContent = '\u2014';
        whyEl.textContent = 'Every size in your range is currently sold out.';
        applyBtn.hidden = true;
        out.classList.add('is-on');
        return;
      }
      var cell = rows[pick];
      var name = cell.size;
      sizeEl.textContent = name;

      var label = shapeLabel(shape);
      var article = /^[aeiou]/.test(label) ? 'an' : 'a';
      var part = g.key === 'waist' ? 'waist' : 'chest';
      var why = 'Based on your height, weight and ' + label + ' build, your ' + part + ' is about ' + inch(g.body) + '. ';
      if (g.kind === 'elastic') {
        why += 'In ' + name + ' this piece has an elastic waist that fits about ' + inch(cell.lo) + '\u2013' + inch(cell.hi) + '.';
      } else {
        var ease = cell.lo - g.body;
        why += 'In ' + name + ' it measures ' + inch(cell.lo) + ' around' +
          (ease >= 0.5 ? ', about ' + inch(ease) + ' of room' : '') + ': ' + window.ZorFitMath.feel(ease) + '.';
      }
      if (pref > 0) why += ' Sized up one for the looser fit you asked for.';
      if (pref < 0) why += ' Sized down one for the closer fit you asked for.';
      if (g.beyondTop) why += ' You may be above this style\u2019s size range: ' + rows[rows.length - 1].size + ' is the largest we make.';
      if (g.belowBottom) why += ' You may be below this style\u2019s size range: ' + rows[0].size + ' is the smallest we make.';
      if (pick !== g.index) {
        why += stockOf(rows[g.index].size) === null
          ? ' This piece is not made in ' + rows[g.index].size + ', so this is the closest we offer.'
          : ' Your closest size is sold out, so this is the nearest available.';
      }
      whyEl.textContent = why;

      applyBtn.hidden = false;
      applyBtn.setAttribute('data-size', name);
      out.classList.add('is-on');
      modal.querySelectorAll('.zor-chart tr[data-size]').forEach(function (tr) {
        tr.classList.toggle('is-rec', tr.getAttribute('data-size') === name);
      });
    }

    if (go) go.addEventListener('click', recommend);

    /* ---------- apply the recommendation to the PDP picker ---------- */
    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        var name = applyBtn.getAttribute('data-size');
        var picked = null;
        document.querySelectorAll('.zor-opt__input').forEach(function (input) {
          if (input.value.toUpperCase() === String(name).toUpperCase()) picked = input;
        });
        if (picked) {
          picked.checked = true;
          picked.dispatchEvent(new Event('change', { bubbles: true }));
        }
        close();
      });
    }
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
